import React from 'react';
import { Box, Typography, styled, ThemeProvider, Button } from '@mui/material';
import { Reward } from '../../../GameRules/GameRulesScreen.view';
import { CoinsIcon } from '../../../../../components/GamifyIcon';
import { useTheme } from 'application/context/PermissionsContext/ThemeContext/ThemeContext';
import { RewardsGrid } from '../../../../games/NewGame/GameFormComponents';
import { useNavigate } from 'react-router-dom';

// Creating custom RewardCard and PointsRewardCard instead of using the ones from GameFormComponents
// because we need to provide theme directly here
const RewardCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.custom.background2,
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: theme.shadows[2],
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

const PointsRewardCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.custom.background2,
  borderRadius: '12px',
  padding: '16px',
  boxShadow: theme.shadows[2],
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

const RewardImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: '200px',
  objectFit: 'cover',
  backgroundColor: theme.custom.background3,
  borderBottom: `1px solid ${theme.custom.stroke}`,
}));

const RewardContent = styled(Box)({
  padding: '12px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const RewardHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const PlaceText = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 600,
  color: theme.custom.text,
}));

const PointsText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: theme.custom.textLight,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}));

interface Props {
  rewards: Reward[];
  isPointsGame?: boolean;
}

const getOrdinalSuffix = (place: number): string => {
  if (place > 3 && place < 21) return 'th';
  switch (place % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

const Rewards: React.FC<Props> = ({ rewards, isPointsGame = false }) => {
  const { theme } = useTheme();
  const navigate = useNavigate();

  if (!rewards || rewards.length === 0) {
    return null;
  }

  const sortedRewards = [...rewards].sort(
    (a, b) => (a.place || 0) - (b.place || 0)
  );

  const renderArchiveButton = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
      <Button
        variant="outlined"
        onClick={() => navigate('/performance/rewards/archive')}
        sx={{
          color: theme.text,
          borderColor: theme.stroke,
          '&:hover': {
            borderColor: theme.primary,
            color: theme.primary,
          },
        }}
      >
        View Archive
      </Button>
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <Box>
        <RewardsGrid>
          {sortedRewards.map((reward) =>
            isPointsGame ? (
              <PointsRewardCard key={reward.id}>
                <PlaceText>
                  {reward.place}
                  {getOrdinalSuffix(reward.place)} place
                </PlaceText>
                <PointsText>
                  <CoinsIcon color={theme.primary} />
                  Earn {reward.points} points winning this challenge
                </PointsText>
              </PointsRewardCard>
            ) : (
              <RewardCard key={reward.id}>
                <RewardImage
                  src={reward.imageName || '/images/reward-placeholder.png'}
                  alt={reward.name || `${reward.points} points`}
                />
                <RewardContent>
                  <RewardHeader>
                    <PlaceText>
                      {reward.place}
                      {getOrdinalSuffix(reward.place)} place
                    </PlaceText>
                    {/* <StockText>{reward.remainingCount} in stock</StockText> */}
                  </RewardHeader>
                  {/* <RewardPoints>{reward.purchaseCount} redeemed</RewardPoints> */}
                </RewardContent>
              </RewardCard>
            )
          )}
        </RewardsGrid>
        {renderArchiveButton()}
      </Box>
    </ThemeProvider>
  );
};

export default Rewards;
